import { useMemo, useState } from "react";
import { ContainerGroupPicker, ContainerGroupPick } from "@racoon/ui";
import { Namespace, TranslationKey, useT, useTypedT } from "@racoon/i18n";
import { ConnectProto, JobType, jobTypeToNumber } from "@racoon/api";
import { useCompatibleContainerGroups } from "~/shared/hooks/use-compatible-container-groups";
import { useUnpricedGroupSchedules } from "~/shared/api/container-group-schedule/use-location-schedules";
import { canTruckServeJobType } from "~/pages/dashboard/pickup-location-groups/model/helpers";
import { containerGroupRequiredJobTypes } from "~/shared/lib/container-group-job-types";
import { getJobCategoryForJobType } from "~/pages/dashboard/routes/hooks/msw-route-list/jobCategoryRouteCompatibility";
import { PseudoJobPick, pseudoJobKey } from "../../model/pseudo-jobs";

/** The category a "JOB_TYPE_*" belongs to. */
const jobCategoryOf = (jobType: string) =>
  getJobCategoryForJobType(jobTypeToNumber(jobType as JobType) as ConnectProto.JobType);

export interface AddPseudoJobsContentProps {
  ownerOrganizationId?: bigint;
  /** The route's division: its groups and its zone's are offered. */
  routeOwnerId?: bigint;
  /** The route's truck; only work it can actually perform is offered. */
  truckId?: bigint;
  /** Its custom truck type, whose capability matrix narrows the job types. */
  truckTypeId?: bigint;
  locations: ConnectProto.PickupLocation[];
  /** `pseudoJobKey`s already in the route, so a stop can't be added twice. */
  existingKeys: Set<string>;
  /** The category the route is already fixed to; nothing outside it is offered. */
  allowedJobCategory?: ConnectProto.JobCategory;
  onAdd: (picks: PseudoJobPick[]) => void;
  /** Closes the modal; the picker's state dies with the unmount. */
  onClose: () => void;
}

/**
 * Picks the stops of a pseudo-route: a pickup location, its container groups,
 * and the work done on each. The picking itself is the shared
 * `ContainerGroupPicker` — the same unit is chosen as when binding container
 * groups to a pickup location group — except the result becomes draft jobs.
 */
export const AddPseudoJobsContent = ({
  ownerOrganizationId,
  routeOwnerId,
  truckId,
  truckTypeId,
  locations,
  existingKeys,
  allowedJobCategory,
  onAdd,
  onClose,
}: AddPseudoJobsContentProps) => {
  const t = useTypedT(Namespace.Routes);
  const tAny = useT();

  const hasTruck = truckId != null && truckId !== 0n;
  const {
    containerGroups,
    truckCapabilities,
    hasCapabilityMatrix,
    areCapabilitiesReady,
    isLoading,
  } = useCompatibleContainerGroups({
    ownerOrganizationId,
    routeOwnerId,
    truckId,
    truckTypeId,
  });

  // Fallback job types of groups whose contract prices none.
  const { schedulesByCgId, hintKey } = useUnpricedGroupSchedules(containerGroups);

  // While the route is still empty, the first pick of this pass fixes its category.
  const [picks, setPicks] = useState<ContainerGroupPick[]>([]);

  const activeJobCategory =
    allowedJobCategory ?? (picks.length > 0 ? jobCategoryOf(picks[0].jobType) : undefined);

  // Job types still addable per container group: the ones its contract prices,
  // that the truck can perform, of the route's category, minus what the route
  // already carries. Nothing at all until the capability matrix is known — an
  // empty one read as "no restrictions" would offer work the truck cannot do.
  const jobTypesByCgId = useMemo(() => {
    const map = new Map<string, string[]>();
    if (!areCapabilitiesReady) return map;

    containerGroups.forEach((cg) => {
      map.set(
        String(cg.id),
        containerGroupRequiredJobTypes(cg, schedulesByCgId.get(String(cg.id))).filter(
          (jobType) =>
            !existingKeys.has(pseudoJobKey(cg.id, jobType)) &&
            (!hasCapabilityMatrix ||
              canTruckServeJobType(truckCapabilities, cg.containerType?.id, jobType)) &&
            (activeJobCategory == null || jobCategoryOf(jobType) === activeJobCategory),
        ),
      );
    });
    return map;
  }, [
    containerGroups,
    schedulesByCgId,
    existingKeys,
    truckCapabilities,
    hasCapabilityMatrix,
    areCapabilitiesReady,
    activeJobCategory,
  ]);

  const locationById = useMemo(
    () => new Map(locations.map((location) => [String(location.id), location])),
    [locations],
  );

  const handleSubmit = (submitted: ContainerGroupPick[]) => {
    onAdd(
      submitted.map<PseudoJobPick>(({ containerGroup, jobType }) => ({
        containerGroup,
        pickupLocation: locationById.get(String(containerGroup.pickupLocationId)),
        jobType,
      })),
    );
    onClose();
  };

  return (
    <>
      {/* As in the bind modal: unpriced groups offer nothing until their schedules arrive. */}
      {hintKey && (
        <p className="pb-2 text-sm text-text-secondary">
          {tAny(Namespace.PickupLocation, hintKey)}
        </p>
      )}
      <ContainerGroupPicker
        locations={locations}
        containerGroups={containerGroups}
        jobTypesByCgId={jobTypesByCgId}
        emptyHint={!hasTruck ? t("addPseudoJobsSelectTruckFirst") : isLoading ? t("loading") : null}
        onSubmit={handleSubmit}
        onPicksChange={setPicks}
        labels={{
          searchPlaceholder: tAny(Namespace.Common, "search"),
          location: t("addPseudoJobsLocation"),
          containerGroups: t("addPseudoJobsContainerGroups"),
          jobTypes: t("addPseudoJobsJobTypes"),
          noLocations: tAny(Namespace.Common, "noData"),
          selectLocation: t("addPseudoJobsSelectLocation"),
          noContainerGroups: t("addPseudoJobsNoContainerGroups"),
          selectContainerGroup: t("addPseudoJobsSelectContainerGroup"),
          submit: t("addPseudoJobsSubmit"),
          submitWithCount: (count) => t("addPseudoJobsSubmitWithCount", { count }),
          containersCount: (count) => t("containersCountShort", { count }),
          jobTypeName: (jobType) => tAny(Namespace.Enum, jobType as TranslationKey),
        }}
      />
    </>
  );
};
